/**
 * HR "History" tab — /hr/coverage?tab=history (capacityChangeRequests).
 *
 * Read-only, permanent history of every recorded capacity change: team,
 * requesting manager, why, old→new cap/floor, effectivity, recorded-by + when,
 * and supporting-document links. Records never disappear.
 */
import { useQuery } from '@tanstack/react-query'

import { capacityRequestDocUrl, listCapacityRequests } from '../../api/capacity-requests'
import { Card } from '../../components/primitives/Card'
import { changeLabel, effectivityLabel } from './capacity-format'

export function CapacityHistoryTab(): JSX.Element {
  const historyQuery = useQuery({
    queryKey: ['capacity-requests'],
    queryFn: () => listCapacityRequests(),
    staleTime: 10_000,
  })
  const history = historyQuery.data ?? []

  return (
    <div style={{ maxWidth: 720 }} data-testid="capacity-history">
      <p style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 20, lineHeight: 1.5 }}>
        Every recorded capacity change, newest first. This is a permanent log,
        records are never removed.
      </p>

      {history.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--text-3)' }}>No capacity changes recorded yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {history.map((r) => (
            <Card key={r.id} padding={16}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{r.teamName}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                  {new Date(r.createdAt).toLocaleDateString()} · by {r.recordedByName ?? 'HR'}
                </div>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4 }}>
                {changeLabel(r)} · <strong>{effectivityLabel(r)}</strong>
              </div>
              {r.requestedByName ? (
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
                  Requested by {r.requestedByName}
                </div>
              ) : null}
              <div style={{ fontSize: 13, color: 'var(--text)', marginTop: 6 }}>{r.reason}</div>
              {r.documents.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {r.documents.map((d) => (
                    <a
                      key={d.id}
                      href={capacityRequestDocUrl(r.id, d.id)}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        fontSize: 12,
                        padding: '3px 8px',
                        borderRadius: 999,
                        border: '1px solid var(--border)',
                        color: 'var(--primary)',
                        textDecoration: 'none',
                      }}
                    >
                      📎 {d.filename}
                    </a>
                  ))}
                </div>
              ) : null}
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
